import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	inject,
	Input,
	input,
	model,
	OnChanges,
	OnInit,
	output,
	PLATFORM_ID,
	ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { DropdownPosition, NgSelectModule } from '@ng-select/ng-select';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	of,
	ReplaySubject,
	switchMap,
} from 'rxjs';

import { BaseDirective } from '@valk-nx/core/lib/directives/base/base.directive';

import { DropdownOption } from './dropdown.interface';
import { equalDropDownOptions } from './dropdown-options';

type VariantType = 'attached' | 'integrated' | 'detached';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	exportAs: 'vpDropdown',
	host: { ngSkipHydration: 'true' },
	selector: `vp-dropdown`,
	styleUrls: ['./scss/dropdown.scss'],
	templateUrl: './dropdown.html',
	imports: [
		AngularSvgIconModule,
		CommonModule,
		NgSelectModule,
		FormsModule,
		ReactiveFormsModule,
	],
})
export class DropdownComponent
	extends BaseDirective
	implements OnInit, OnChanges, AfterViewChecked
{
	private readonly platformId = inject(PLATFORM_ID);

	className = input<string>('');
	hasError = input<boolean>(false);
	isValid = input<boolean>(false);
	isRequired = input<boolean>(false);
	centered = input<boolean>(false);
	selectData = input.required<DropdownOption[]>();
	variant = input<VariantType>('attached');
	inputId = input<string>('');
	label = input<string>('');
	control = input(new FormControl());
	searchable = input<boolean>(false);
	useAutoComplete = input<boolean>(true);
	dropdownPosition = input<DropdownPosition>('bottom');
	isWhite = input<boolean>(false);
	useAsFormControl = input<boolean>(false);
	downIcon = input<string>('icon-chevron-down');
	upIcon = input<string>('icon-chevron-up');
	_placeholder$ = new BehaviorSubject<string>(
		'components.dropdown.placeholder',
	);
	@Input() set placeholder(value: string) {
		this._placeholder$.next(value);
	}

	value = model.required<string>();

	selectedValue = output<string>();

	translatedPlaceholder$!: Observable<string>;
	selectOptions$ = new Observable<DropdownOption[]>();
	private readonly selectData$ = new ReplaySubject<DropdownOption[]>();
	selectedDropdownPlaceholder$!: Observable<string>;

	@HostBinding('style')
	get hostStyle(): Record<string, string> {
		const down = `url('https://assets.vandervalkonline.com/live/assets/icons/${this.downIcon()}.svg')`;
		const up = `url('https://assets.vandervalkonline.com/live/assets/icons/${this.upIcon()}.svg')`;
		return {
			'--down-icon': down,
			'--up-icon': up,
		};
	}

	constructor() {
		super();
	}

	ngOnInit(): void {
		this.selectOptions$ = this.selectData$.pipe(
			distinctUntilChanged(equalDropDownOptions),
			filter(
				(options) =>
					!!options &&
					options.some((option) => option.label !== undefined),
			),
			switchMap((options: DropdownOption[]) => {
				/* istanbul ignore next */
				if (options.length === 0) {
					return of([]);
				}
				const optionLabels = options.map((option) => option.label);
				const translatedLabels$ = this.translate
					.stream(optionLabels)
					.pipe(
						map((translations) => {
							return options.map((option) => {
								return {
									...option,
									label: translations[option.label],
								};
							});
						}),
					);
				const selectedLabels = options
					.map((option) => option.selectLabel)
					.filter((value) => value !== undefined);
				const translatedSelectLabel$ =
					selectedLabels.length > 0
						? this.translate
								.stream(selectedLabels as string[])
								.pipe(
									map((translations) => {
										return options.map((option) => {
											return {
												...option,
												selectLabel:
													translations[
														option.selectLabel as string
													],
											};
										});
									}),
								)
						: of(options);

				return combineLatest([
					translatedLabels$,
					translatedSelectLabel$,
				]).pipe(
					map(([labels, selectLabels]) => {
						return options.map((option, index) => {
							return {
								...option,
								label: labels[index].label,
								selectLabel: selectLabels[index]?.selectLabel,
							};
						});
					}),
				);
			}),
		);

		this.translatedPlaceholder$ = this._placeholder$.pipe(
			switchMap((placeholder) => {
				return placeholder
					? this.translate.stream(placeholder)
					: of('');
			}),
		);

		this.selectedDropdownPlaceholder$ = this.selectOptions$.pipe(
			map((options) => {
				const selectedValue = options.find(
					(option) => option.value === this.value(),
				)?.label;
				return selectedValue ?? this.value();
			}),
		);
	}

	ngAfterViewChecked() {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.useAutoComplete() &&
			document.getElementById(this.inputId())
		) {
			// Ng-select doesnt allow to change autocomplete property on the input
			document
				.getElementById(this.inputId())!
				.setAttribute('autocomplete', 'not-autocomplete');
		}
	}

	ngOnChanges(): void {
		this.selectData$.next(this.selectData());
	}

	update() {
		this.selectedValue.emit(this.value());
	}
}
